/*
 * Casavono : recherche instantanee (25/09/26).
 * <casa-search data-mode="bar">   barre du header (desktop), panneau sous la barre
 * <casa-search data-mode="sheet"> meme recherche en plein ecran, ouverte par la loupe du header (mobile, ou desktop sans barre)
 * Resultats = section casa-search-results rendue par Shopify (/search/suggest?section_id=...), donc prix, traductions et
 * liens localises viennent du serveur. Requetes precedentes annulees, petit cache, clavier (fleches, Entree, Echap), « / ».
 */
(() => {
  if (customElements.get('casa-search')) return;

  const routes = (window.Theme && window.Theme.routes) || {};
  const SUGGEST = routes.predictive_search_url || '/search/suggest';
  const SEARCH = routes.search_url || '/search';
  const EXAMPLES = ['Search “navy bedroom”', 'Search “peel and stick bathroom”', 'Search “dark floral”', 'Search “nursery animals”', 'Search murals, rooms, colours…'];
  const cache = new Map();

  class CasaSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-csr-input]');
      this.panel = this.querySelector('[data-csr-panel]');
      this.results = this.querySelector('[data-csr-results]');
      this.emptyTpl = this.querySelector('[data-csr-empty]');
      this.form = this.input && this.input.form;
      this.isSheet = this.dataset.mode === 'sheet';
      this.active = -1;
      this.timer = 0;
      this.ctrl = null;
      this.lastTerm = null;
      if (!this.input || !this.panel) return;

      if (this.isSheet && this.parentElement !== document.body) {
        // le header est masque par morceaux sur mobile : le plein ecran doit vivre dans <body>
        document.body.appendChild(this);
        return; // connectedCallback est rappele apres le deplacement
      }

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.panel.addEventListener('mousemove', (e) => {
        const a = e.target.closest('[data-csr-item]');
        if (a) this.setActive(this.items().indexOf(a), false);
      });
      if (this.isSheet) {
        this.querySelector('[data-csr-close]').addEventListener('click', () => this.closeSheet());
      } else {
        document.addEventListener('pointerdown', (e) => { if (!this.contains(e.target)) this.close(); });
        this.rotatePlaceholder();
      }
    }

    /* ---------- ouverture / fermeture ---------- */
    open() {
      if (!this.input.value.trim()) this.showEmpty();
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }
    close() {
      if (this.isSheet) return;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.setActive(-1);
    }
    openSheet() {
      this.hidden = false;
      document.documentElement.classList.add('casa-search-lock');
      this.input.focus(); // dans le geste de l'utilisateur, sinon iOS n'ouvre pas le clavier
      requestAnimationFrame(() => this.classList.add('is-visible'));
      this.open();
    }
    closeSheet() {
      this.classList.remove('is-visible');
      document.documentElement.classList.remove('casa-search-lock');
      setTimeout(() => { this.hidden = true; }, 200);
    }

    /* ---------- etat vide : raccourcis + vus recemment ---------- */
    showEmpty() {
      if (this.lastTerm === '') return;
      this.lastTerm = '';
      this.results.innerHTML = this.emptyTpl ? this.emptyTpl.innerHTML : '';
      this.setActive(-1);
      this.loadRecent();
    }
    async loadRecent() {
      let ids = [];
      try { ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]'); } catch (e) { ids = []; }
      ids = ids.filter((x) => /^\d+$/.test(String(x))).slice(0, 4);
      const box = this.results.querySelector('[data-csr-recent]');
      if (!ids.length || !box) return;
      const url = new URL(SEARCH, location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', this.dataset.section);
      try {
        const html = await this.fetchHtml(url.toString());
        if (this.lastTerm !== '' || !html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const csr = doc.querySelector('.csr');
        if (csr && csr.querySelector('.csr__card')) { box.innerHTML = csr.innerHTML; box.hidden = false; }
      } catch (e) { /* les vus recemment sont un bonus */ }
    }

    /* ---------- frappe ---------- */
    onInput() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();
      if (!term) { this.showEmpty(); return; }
      this.timer = setTimeout(() => this.search(term), 160);
    }
    async search(term) {
      if (term === this.lastTerm) return;
      this.lastTerm = term;
      const url = new URL(SUGGEST, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', this.dataset.section);
      url.searchParams.set('resources[type]', 'product,collection,query,article,page');
      url.searchParams.set('resources[limit]', '10'); // 10 = maximum Shopify, la section en reclasse et en garde 6
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      this.classList.add('is-loading');
      try {
        const html = await this.fetchHtml(url.toString(), true);
        if (html == null || term !== this.lastTerm) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const csr = doc.querySelector('.csr');
        this.results.innerHTML = csr ? csr.outerHTML : '';
        this.setActive(-1);
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        this.track(term, csr ? parseInt(csr.dataset.count || '0', 10) : 0);
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      } finally {
        if (term === this.lastTerm) this.classList.remove('is-loading');
      }
    }
    async fetchHtml(url, abortable) {
      if (cache.has(url)) return cache.get(url);
      let signal;
      if (abortable) { if (this.ctrl) this.ctrl.abort(); this.ctrl = new AbortController(); signal = this.ctrl.signal; }
      const res = await fetch(url, { signal, headers: { Accept: 'text/html' } });
      if (!res.ok) return null;
      const html = await res.text();
      if (cache.size > 60) cache.delete(cache.keys().next().value);
      cache.set(url, html);
      return html;
    }

    /* ---------- clavier ---------- */
    items() { return Array.from(this.results.querySelectorAll('[data-csr-item]')).filter((a) => a.offsetParent !== null); }
    setActive(i, scroll = true) {
      const list = this.items();
      list.forEach((a) => a.classList.remove('is-active'));
      this.active = i;
      if (i >= 0 && list[i]) {
        list[i].classList.add('is-active');
        if (scroll) list[i].scrollIntoView({ block: 'nearest' });
      }
    }
    onKey(e) {
      const list = this.items();
      if (e.key === 'ArrowDown') { e.preventDefault(); this.open(); this.setActive(Math.min(this.active + 1, list.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.setActive(Math.max(this.active - 1, -1)); }
      else if (e.key === 'Enter' && this.active >= 0 && list[this.active]) { e.preventDefault(); location.href = list[this.active].href; }
      else if (e.key === 'Escape') { if (this.isSheet) this.closeSheet(); else { this.close(); this.input.blur(); } }
    }

    /* ---------- exemples dans la barre (desktop) ---------- */
    rotatePlaceholder() {
      let i = 0;
      setInterval(() => {
        if (document.activeElement === this.input || this.input.value) return;
        i = (i + 1) % EXAMPLES.length;
        this.input.setAttribute('placeholder', EXAMPLES[i]);
      }, 3200);
    }

    /* ---------- mesure : evenement Shopify pour les pixels (recherches sans resultat comprises) ---------- */
    track(term, count) {
      clearTimeout(this.trackTimer);
      this.trackTimer = setTimeout(() => {
        try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('casa_search_typed', { term, results: count }); } catch (e) {}
      }, 1200);
    }
  }
  customElements.define('casa-search', CasaSearch);

  /* La loupe du header ouvre notre recherche a la place de la fenetre d'Horizon :
     barre visible (desktop) -> on la met au point ; sinon -> plein ecran. */
  window.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('search-button button');
    if (!btn) return;
    const bar = document.querySelector('casa-search[data-mode="bar"]');
    const sheet = document.querySelector('casa-search[data-mode="sheet"]');
    const barVisible = bar && bar.offsetParent !== null && bar.getBoundingClientRect().width > 0;
    if (!barVisible && !sheet) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (barVisible) { const i = bar.querySelector('[data-csr-input]'); i.focus(); i.select(); }
    else sheet.openSheet();
  }, true);

  /* « / » met la recherche au point (desktop), comme sur les grands sites */
  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    const i = document.querySelector('casa-search[data-mode="bar"] [data-csr-input]');
    if (i && i.offsetParent !== null) { e.preventDefault(); i.focus(); }
  });
})();
